import { useEffect, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { z } from 'zod';
import { CrudTable, type CrudColumn } from '@/shared/components/ui/crud-table';
import { useIsAdminTier } from '@/shared/auth/SessionProvider';
import { StatusBadge } from '@/shared/components/ui/status-badge';
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/shared/components/ui/dialog';
import { Button } from '@/shared/components/ui/button';
import { Input } from '@/shared/components/ui/input';
import { Label } from '@/shared/components/ui/label';
import { DatePicker } from '@/shared/components/ui/date-picker';
import { Combobox } from '@/shared/components/ui/combobox';
import {
  arToIso,
  campaniaDisplayStatus,
  createCampania,
  isCampaniaVigente,
  listAllCampanias,
  softDeleteCampania,
  storedDecimalToPercent,
  updateCampania,
  type CampaniaItem,
} from '@/shared/api/controllers/campanias';
import {
  listActiveCotizableArticulos,
  type ArticuloItem,
} from '@/shared/api/controllers/articulos';
import { useActiveCountry } from '@/shared/countries/ActiveCountryProvider';
import type { EntityViewProps } from '../../types';

const QUERY_KEY = ['configuracion', 'campanias'] as const;

type Mode =
  | { kind: 'closed' }
  | { kind: 'create' }
  | { kind: 'edit'; item: CampaniaItem }
  | { kind: 'delete'; item: CampaniaItem };

const schema = z
  .object({
    nombre: z.string().trim().min(1, 'Ingresá el nombre'),
    articuloId: z.string().min(1, 'Seleccioná un artículo'),
    articuloNombre: z.string(),
    descuentoPct: z.coerce
      .number({ invalid_type_error: 'Número inválido' })
      .gt(0, 'El descuento debe ser mayor a 0')
      .lte(100, 'El descuento no puede superar 100 %'),
    desde: z.string().min(1, 'Seleccioná la fecha desde'),
    hasta: z.string().min(1, 'Seleccioná la fecha hasta'),
  })
  .superRefine((v, ctx) => {
    if (v.desde && v.hasta && v.desde > v.hasta) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['hasta'],
        message: 'La fecha hasta debe ser posterior a desde',
      });
    }
  });

type Values = z.infer<typeof schema>;

const empty: Values = {
  nombre: '',
  articuloId: '',
  articuloNombre: '',
  descuentoPct: 0,
  desde: '',
  hasta: '',
};

function itemToForm(item: CampaniaItem): Values {
  return {
    nombre: item.fields.NombreCampania_C ?? '',
    articuloId: item.fields.ArticuloID_C ?? '',
    articuloNombre: item.fields.Articulo_C ?? '',
    descuentoPct: storedDecimalToPercent(item.fields.Descuento_C) ?? 0,
    desde: arToIso(item.fields.Desde_C),
    hasta: arToIso(item.fields.Hasta_C),
  };
}

export function CampaniasEntity({
  search,
  filterValue,
  onStatus,
  openCreate,
  onCreateHandled,
}: EntityViewProps) {
  const queryClient = useQueryClient();
  const { config: activeCountry } = useActiveCountry();
  const scopedKey = useMemo(
    () => [...QUERY_KEY, activeCountry.code] as readonly unknown[],
    [activeCountry.code],
  );
  const { data: rows = [], isPending, isFetching, error, refetch } = useQuery({
    queryKey: scopedKey,
    queryFn: listAllCampanias,
    staleTime: 60_000,
  });
  const [mode, setMode] = useState<Mode>({ kind: 'closed' });

  useEffect(() => {
    if (openCreate) {
      setMode({ kind: 'create' });
      onCreateHandled();
    }
  }, [openCreate, onCreateHandled]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    let sorted = [...rows].sort((a, b) =>
      (a.fields.NombreCampania_C ?? '').localeCompare(b.fields.NombreCampania_C ?? '', 'es'),
    );
    if (filterValue) {
      sorted = sorted.filter((r) => campaniaDisplayStatus(r.fields) === filterValue);
    }
    if (!q) return sorted;
    return sorted.filter((r) => {
      const n = (r.fields.NombreCampania_C ?? '').toLowerCase();
      const a = (r.fields.Articulo_C ?? '').toLowerCase();
      return n.includes(q) || a.includes(q);
    });
  }, [rows, search, filterValue]);

  const columns: CrudColumn<CampaniaItem>[] = [
    {
      header: 'Status',
      width: 'w-32',
      render: (r) => <StatusBadge status={campaniaDisplayStatus(r.fields)} />,
    },
    {
      header: 'Nombre',
      render: (r) => (
        <span className="font-medium text-foreground">{r.fields.NombreCampania_C || '—'}</span>
      ),
    },
    {
      header: 'Artículo',
      render: (r) => <span className="text-muted-foreground">{r.fields.Articulo_C || '—'}</span>,
    },
    {
      header: 'Descuento',
      render: (r) => {
        const pct = storedDecimalToPercent(r.fields.Descuento_C);
        return pct == null ? '—' : `${pct} %`;
      },
    },
    {
      header: 'Desde',
      render: (r) => r.fields.Desde_C || '—',
    },
    {
      header: 'Hasta',
      render: (r) => r.fields.Hasta_C || '—',
    },
  ];

  const closeDialog = () => setMode({ kind: 'closed' });
  const invalidate = () => queryClient.invalidateQueries({ queryKey: QUERY_KEY });

  const canEdit = useIsAdminTier();
  return (
    <>
      <CrudTable
        hideActions={!canEdit}
        rows={filtered}
        columns={columns}
        loading={isPending}
        error={error instanceof Error ? error : null}
        onRetry={() => refetch()}
        emptyMessage={search ? `No hay resultados para "${search}".` : 'Sin campañas.'}
        onEdit={(r) => setMode({ kind: 'edit', item: r })}
        onDelete={(r) => setMode({ kind: 'delete', item: r })}
        canEdit={(r) => campaniaDisplayStatus(r.fields) === 'Activo'}
        editDisabledTitle={(r) =>
          campaniaDisplayStatus(r.fields) === 'Finalizado'
            ? 'La campaña ya finalizó'
            : 'El registro está inactivo'
        }
        canDelete={(r) => campaniaDisplayStatus(r.fields) === 'Activo'}
        deleteDisabledTitle={(r) =>
          campaniaDisplayStatus(r.fields) === 'Finalizado'
            ? 'La campaña ya finalizó'
            : 'El registro ya está inactivo'
        }
        isBackgroundFetching={isFetching}
      />

      {(mode.kind === 'create' || mode.kind === 'edit') && (
        <CampaniaDialog
          open
          onOpenChange={(o) => !o && closeDialog()}
          item={mode.kind === 'edit' ? mode.item : null}
          existingRows={rows}
          onSuccess={(msg) => {
            invalidate();
            closeDialog();
            onStatus({ kind: 'success', message: msg });
          }}
          onError={(msg) => {
            closeDialog();
            onStatus({ kind: 'error', message: msg });
          }}
        />
      )}

      {mode.kind === 'delete' && (
        <DeleteCampaniaDialog
          open
          onOpenChange={(o) => !o && closeDialog()}
          item={mode.item}
          onSuccess={(msg) => {
            invalidate();
            closeDialog();
            onStatus({ kind: 'success', message: msg });
          }}
          onError={(msg) => {
            closeDialog();
            onStatus({ kind: 'error', message: msg });
          }}
        />
      )}
    </>
  );
}

function CampaniaDialog({
  open,
  onOpenChange,
  item,
  existingRows,
  onSuccess,
  onError,
}: {
  open: boolean;
  onOpenChange: (o: boolean) => void;
  item: CampaniaItem | null;
  existingRows: CampaniaItem[];
  onSuccess: (msg: string) => void;
  onError: (msg: string) => void;
}) {
  const isEdit = Boolean(item);
  const { config: activeCountry } = useActiveCountry();
  const form = useForm<Values>({
    resolver: zodResolver(schema),
    defaultValues: item ? itemToForm(item) : empty,
  });

  useEffect(() => {
    if (open) form.reset(item ? itemToForm(item) : empty);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, item?.id]);

  const { data: articulos = [] } = useQuery({
    queryKey: ['configuracion', 'articulos', 'active', 'cotizable', activeCountry.code],
    queryFn: listActiveCotizableArticulos,
    staleTime: 60_000,
  });

  const mutation = useMutation({
    mutationFn: async (v: Values) => {
      const payload = {
        nombre: v.nombre,
        articuloId: v.articuloId,
        articuloNombre: v.articuloNombre,
        descuentoPct: v.descuentoPct,
        desde: v.desde,
        hasta: v.hasta,
      };
      if (isEdit && item) return updateCampania(item.id, payload);
      return createCampania(payload);
    },
    onSuccess: () =>
      onSuccess(
        isEdit ? 'La campaña se actualizó correctamente.' : 'La campaña se creó correctamente.',
      ),
    onError: (err) => onError(err instanceof Error ? err.message : 'No se pudo guardar la campaña.'),
  });

  const onSubmit = form.handleSubmit((v) => {
    const norm = v.nombre.trim().toLowerCase();
    // A campaign can span multiple articles (one junction row each), so the
    // real conflict is the same (nombre + artículo) pair — not the name
    // alone. Guarding on name blocked legitimate multi-article campaigns and
    // false-positived when editing one row of a multi-article campaign.
    const duplicated = existingRows.find(
      (r) =>
        r.id !== item?.id &&
        (r.fields.NombreCampania_C ?? '').trim().toLowerCase() === norm &&
        (r.fields.ArticuloID_C ?? '') === v.articuloId,
    );
    if (duplicated) {
      form.setError('articuloId', {
        type: 'manual',
        message: 'Esta campaña ya incluye ese artículo.',
      });
      return;
    }
    mutation.mutate(v);
  });

  // When editing a campaign that spans multiple articles, discount/date
  // edits apply to every article (they're campaign-level columns). Surface
  // that so the change isn't silently broader than the single row shown.
  const siblingArticleCount = useMemo(() => {
    if (!item) return 0;
    const norm = (item.fields.NombreCampania_C ?? '').trim().toLowerCase();
    return existingRows.filter(
      (r) => (r.fields.NombreCampania_C ?? '').trim().toLowerCase() === norm,
    ).length;
  }, [item, existingRows]);

  const fieldError = (name: keyof Values) =>
    form.formState.errors[name]?.message as string | undefined;

  const articuloId = form.watch('articuloId');
  const selectedArticulo = useMemo(
    () => articulos.find((a) => a.id === articuloId) ?? null,
    [articulos, articuloId],
  );

  // Flag articles that already carry a currently-vigente campaign so admins
  // don't unknowingly double-book one. Keyed by the article's list-item id
  // (ArticuloID_C), excluding the row being edited.
  const vigenteCampaignByArticulo = useMemo(() => {
    const map = new Map<string, number>();
    for (const r of existingRows) {
      if (r.id === item?.id) continue;
      const artId = r.fields.ArticuloID_C ?? '';
      if (!artId || !isCampaniaVigente(r.fields)) continue;
      const pct = storedDecimalToPercent(r.fields.Descuento_C);
      if (pct != null) map.set(artId, pct);
    }
    return map;
  }, [existingRows, item?.id]);
  const watchedNombre = form.watch('nombre');
  const watchedDescuento = form.watch('descuentoPct');
  const watchedDesde = form.watch('desde');
  const watchedHasta = form.watch('hasta');

  const missingFields = useMemo(() => {
    const missing: string[] = [];
    if (!watchedNombre?.trim()) missing.push('Nombre');
    if (!articuloId) missing.push('Artículo');
    if (!watchedDescuento || watchedDescuento <= 0) missing.push('Descuento');
    if (!watchedDesde) missing.push('Desde');
    if (!watchedHasta) missing.push('Hasta');
    return missing;
  }, [watchedNombre, articuloId, watchedDescuento, watchedDesde, watchedHasta]);

  const canSubmit = missingFields.length === 0;

  return (
    <Dialog open={open} onOpenChange={(o) => !mutation.isPending && onOpenChange(o)}>
      <DialogContent
        className="sm:max-w-xl"
        processing={mutation.isPending && (isEdit ? 'Guardando cambios…' : 'Creando campaña…')}
      >
        <DialogHeader>
          <DialogTitle>{isEdit ? 'Editar campaña' : 'Nueva campaña'}</DialogTitle>
          <DialogDescription>
            Definí el artículo, el descuento y la ventana de vigencia.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={onSubmit} className="flex flex-1 flex-col overflow-hidden" noValidate>
          <DialogBody className="space-y-4">
            <div className="space-y-1.5">
              <Label htmlFor="nombre">
                Nombre <span className="text-primary">*</span>
              </Label>
              <Input
                id="nombre"
                variant="filled"
                placeholder="Ingresar nombre de la campaña"
                autoFocus
                {...form.register('nombre')}
              />
              {fieldError('nombre') && (
                <p className="text-xs font-medium text-destructive">{fieldError('nombre')}</p>
              )}
            </div>

            <div className="space-y-1.5">
              <Label>
                Artículo <span className="text-primary">*</span>
              </Label>
              <Combobox<ArticuloItem>
                value={selectedArticulo}
                onChange={(picked) => {
                  form.setValue('articuloId', picked?.id ?? '', {
                    shouldDirty: true,
                    shouldValidate: true,
                  });
                  form.setValue('articuloNombre', picked?.fields.Articulo_ART ?? '', {
                    shouldDirty: true,
                  });
                }}
                items={articulos}
                getKey={(a) => a.id}
                getLabel={(a) => a.fields.Concat_ART || a.fields.Articulo_ART || a.id}
                getSublabel={(a) => {
                  const parts: string[] = [];
                  const codigo = a.fields.Codigo_ART?.trim();
                  const categoria = a.fields.Categoria_ART?.trim();
                  if (codigo) parts.push(codigo);
                  if (categoria) parts.push(categoria);
                  const vigente = vigenteCampaignByArticulo.get(a.id);
                  if (vigente != null) parts.push(`Ya en campaña ${vigente}%`);
                  return parts.join(' · ') || null;
                }}
                getItemClassName={(a) =>
                  vigenteCampaignByArticulo.has(a.id)
                    ? 'bg-amber-50 text-amber-900 hover:bg-amber-100 focus-visible:bg-amber-100'
                    : undefined
                }
                matcher={(a, q) =>
                  (a.fields.Articulo_ART ?? '').toLowerCase().includes(q) ||
                  (a.fields.Codigo_ART ?? '').toLowerCase().includes(q) ||
                  (a.fields.Concat_ART ?? '').toLowerCase().includes(q)
                }
                placeholder="Seleccionar artículo"
              />
              {fieldError('articuloId') && (
                <p className="text-xs font-medium text-destructive">{fieldError('articuloId')}</p>
              )}
            </div>

            {isEdit && siblingArticleCount > 1 && (
              <p className="rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800">
                Esta campaña incluye {siblingArticleCount} artículos. El descuento y
                las fechas se aplican a todos.
              </p>
            )}

            <div className="grid gap-4 md:grid-cols-3">
              <div className="space-y-1.5">
                <Label htmlFor="descuentoPct">
                  Descuento <span className="text-primary">*</span>
                </Label>
                <div className="relative">
                  <Input
                    id="descuentoPct"
                    type="number"
                    step="0.01"
                    variant="filled"
                    className="pr-10"
                    {...form.register('descuentoPct', { valueAsNumber: true })}
                  />
                  <span className="pointer-events-none absolute right-4 top-1/2 -translate-y-1/2 text-sm text-muted-foreground">
                    %
                  </span>
                </div>
                {fieldError('descuentoPct') && (
                  <p className="text-xs font-medium text-destructive">{fieldError('descuentoPct')}</p>
                )}
              </div>

              <div className="space-y-1.5">
                <Label htmlFor="desde">
                  Desde <span className="text-primary">*</span>
                </Label>
                <DatePicker
                  ariaLabel="Desde"
                  className="h-12 rounded-lg px-4 text-[15px]"
                  value={watchedDesde ?? ''}
                  onChange={(v) =>
                    form.setValue('desde', v, { shouldDirty: true, shouldValidate: true })
                  }
                  max={watchedHasta || undefined}
                />
                {fieldError('desde') && (
                  <p className="text-xs font-medium text-destructive">{fieldError('desde')}</p>
                )}
              </div>

              <div className="space-y-1.5">
                <Label htmlFor="hasta">
                  Hasta <span className="text-primary">*</span>
                </Label>
                <DatePicker
                  ariaLabel="Hasta"
                  className="h-12 rounded-lg px-4 text-[15px]"
                  value={watchedHasta ?? ''}
                  onChange={(v) =>
                    form.setValue('hasta', v, { shouldDirty: true, shouldValidate: true })
                  }
                  min={watchedDesde || undefined}
                />
                {fieldError('hasta') && (
                  <p className="text-xs font-medium text-destructive">{fieldError('hasta')}</p>
                )}
              </div>
            </div>
          </DialogBody>
          <DialogFooter>
            {missingFields.length > 0 && (
              <p className="mr-auto text-xs text-muted-foreground">
                Faltan completar: <span className="font-medium text-destructive">{missingFields.join(', ')}</span>
              </p>
            )}
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              disabled={mutation.isPending}
            >
              Cancelar
            </Button>
            <Button type="submit" disabled={mutation.isPending || !canSubmit}>
              {isEdit ? 'Guardar cambios' : 'Añadir'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function DeleteCampaniaDialog({
  open,
  onOpenChange,
  item,
  onSuccess,
  onError,
}: {
  open: boolean;
  onOpenChange: (o: boolean) => void;
  item: CampaniaItem;
  onSuccess: (msg: string) => void;
  onError: (msg: string) => void;
}) {
  const mutation = useMutation({
    mutationFn: async () => softDeleteCampania(item.id),
    onSuccess: () => onSuccess('Campaña eliminada correctamente.'),
    onError: (err) =>
      onError(err instanceof Error ? err.message : 'No se pudo eliminar la campaña.'),
  });

  return (
    <Dialog open={open} onOpenChange={(o) => !mutation.isPending && onOpenChange(o)}>
      <DialogContent
        className="sm:max-w-md"
        processing={mutation.isPending && 'Eliminando campaña…'}
      >
        <DialogHeader>
          <DialogTitle>Eliminar campaña</DialogTitle>
          <DialogDescription>
            ¿Confirmás la eliminación de{' '}
            <strong className="text-foreground">{item.fields.NombreCampania_C || '—'}</strong>?
          </DialogDescription>
        </DialogHeader>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={mutation.isPending}>
            Cancelar
          </Button>
          <Button variant="destructive" onClick={() => mutation.mutate()} disabled={mutation.isPending}>
            Eliminar
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
